import { Component, Suspense, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import ErrorBoundary from "@/components/error-boundary";
import { Loading } from "@/components/ui/loading";
import { ChunkRecovery } from "./chunk-recovery";
import { isChunkLoadError } from "./chunk-load-error";
import { LazyNavigationContext } from "./lazy-with-retry";

type Props = {
  children: ReactNode;
  /** Offer "Ir para Hoje" on an import failure. Off where Hoje is not the
   * reader's place (patient portal, telehealth) and inside the team workspace:
   * a failed window or panel must not throw the user out of the workspace, and
   * leaving for `/` would replace its return address and close the clinic
   * screen kept open behind it. */
  todayAction?: boolean;
};
type BoundaryProps = { children: ReactNode; resetKey: string; todayAction: boolean };
type State = { failed: boolean };

class LazyImportErrorBoundary extends Component<BoundaryProps, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(error: unknown): State | null {
    return isChunkLoadError(error) ? { failed: true } : null;
  }

  componentDidCatch(error: unknown) {
    if (!isChunkLoadError(error)) {
      throw error;
    }
    console.error("[MONITORING] Lazy route chunk failed to load");
  }

  componentDidUpdate(previous: BoundaryProps) {
    if (this.state.failed && previous.resetKey !== this.props.resetKey) {
      this.setState({ failed: false });
    }
  }

  render() {
    if (this.state.failed) return <ChunkRecovery todayAction={this.props.todayAction} />;

    return this.props.children;
  }
}

function RouteLoading() {
  return (
    <div className="flex min-h-48 flex-col items-center justify-center gap-3" role="status">
      <div className="h-16 w-16">
        <Loading />
      </div>
      <span>Carregando…</span>
    </div>
  );
}

export function LazyRouteBoundary({ children, todayAction = true }: Props) {
  const location = useLocation();
  return (
    <ErrorBoundary resetKey={location.key} compact>
      <LazyImportErrorBoundary resetKey={location.key} todayAction={todayAction}>
        <LazyNavigationContext.Provider value={location.key}>
          <Suspense fallback={<RouteLoading />}>{children}</Suspense>
        </LazyNavigationContext.Provider>
      </LazyImportErrorBoundary>
    </ErrorBoundary>
  );
}
